import React from "react";
import { sweetalert } from "../../App";
import ExemptionAPI from "../../network/ExemptionAPI";
import { hideModal } from "../../util/FormatUtil";

type ExportStatus = "pending" | "processing" | "ready" | "failed";

interface ExportJob {
    ID: number;
    Status: ExportStatus;
    ExportType?: string; // 'admin' (Exemption Records) | 'expired' (Expiring 6th Grade)
    RowCount?: number;
    CreatedDate?: string;
    CompletedDate?: string;
    ExpiresAt?: string;
    Filter?: any;
    Error?: string;
}

// Human label + download filename per export type.
function exportTypeLabel(t?: string): string {
    return t === "expired" ? "Expiring 6th Grade" : "Exemption Records";
}
function exportFileName(t?: string): string {
    return t === "expired" ? "Expiring 6th Grade Exemptions.xlsx" : "Exemption Records.xlsx";
}

interface Props {
    filter: any; // the current Exemption Records filter ("export current results")
}

interface State {
    jobs: ExportJob[];
    starting: boolean;
}

export class ExemptionExportsModal extends React.Component<Props, State> {
    public static readonly ID = "exemptionExports";
    private pollInterval: any = null;

    constructor(props: Props) {
        super(props);
        this.state = { jobs: [], starting: false };
        this.refresh = this.refresh.bind(this);
        this.handleStartExport = this.handleStartExport.bind(this);
        this.handleStartExpiredExport = this.handleStartExpiredExport.bind(this);
    }

    componentDidMount() {
        // Refresh + poll only while the modal is open so we don't hit the API in the
        // background when nobody's looking.
        const el = window["$"]("#" + ExemptionExportsModal.ID);
        el.on("shown.bs.modal", () => {
            this.refresh();
            this.startPolling();
        });
        el.on("hidden.bs.modal", () => this.stopPolling());
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    private startPolling() {
        if (!this.pollInterval) {
            this.pollInterval = setInterval(this.refresh, 4000);
        }
    }

    private stopPolling() {
        if (this.pollInterval) { clearInterval(this.pollInterval); this.pollInterval = null; }
    }

    private static hasActiveJobs(jobs: ExportJob[]): boolean {
        return (jobs || []).some(j => j.Status === "pending" || j.Status === "processing");
    }

    async refresh() {
        try {
            const res = await ExemptionAPI.listExports();
            if (res && res.success) {
                const jobs = res.jobs || [];
                this.setState({ jobs });
                // Stop polling once everything is terminal (ready/failed); a new export
                // restarts it. Avoids continuous 4s API load on an idle open tab.
                if (!ExemptionExportsModal.hasActiveJobs(jobs)) {
                    this.stopPolling();
                }
            }
        } catch (e) {
            // keep the last known list; transient errors shouldn't clear the panel
        }
    }

    handleStartExport() {
        sweetalert.fire({
            title: "Should your export include Patient Identifiers?",
            showCloseButton: true,
            showDenyButton: true,
            showCancelButton: false,
            confirmButtonText: "Include",
            denyButtonText: "Don't include",
        }).then(async (result) => {
            if (!(result.isConfirmed || result.isDenied)) return;
            const includePHI = result.isConfirmed;
            const filter = Object.assign({}, this.props.filter || {}, { IncludePHI: includePHI });
            this.setState({ starting: true });
            try {
                const res = await ExemptionAPI.createExport(filter);
                if (!res || !res.success) {
                    sweetalert.fire({ icon: "error", title: "", text: (res && res.reason) || "Failed to start export." });
                }
            } catch (e) {
                sweetalert.fire({ icon: "error", title: "", text: "Failed to start export." });
            } finally {
                this.setState({ starting: false });
                this.startPolling();
                this.refresh();
            }
        });
    }

    // Expiring 6th-Grade export. No PHI prompt — this report always includes parent contact
    // (the server forces it), and it honors the current Exemption Records filters.
    handleStartExpiredExport() {
        this.setState({ starting: true });
        ExemptionAPI.createExport(this.props.filter || {}, "expired")
            .then((res) => {
                if (!res || !res.success) {
                    sweetalert.fire({ icon: "error", title: "", text: (res && res.reason) || "Failed to start export." });
                }
            })
            .catch(() => sweetalert.fire({ icon: "error", title: "", text: "Failed to start export." }))
            .then(() => { this.setState({ starting: false }); this.startPolling(); this.refresh(); });
    }

    async handleDownload(job: ExportJob) {
        try {
            await ExemptionAPI.downloadExport(job.ID, exportFileName(job.ExportType));
        } catch (e) {
            sweetalert.fire({ icon: "error", title: "", text: "Failed to download export. It may have expired." });
        }
    }

    handleRetry(job: ExportJob) {
        let filter = job.Filter;
        if (typeof filter === "string") {
            try { filter = JSON.parse(filter); } catch (e) { filter = {}; }
        }
        this.setState({ starting: true });
        ExemptionAPI.createExport(filter || {}, job.ExportType === "expired" ? "expired" : "admin")
            .then(() => { this.setState({ starting: false }); this.startPolling(); this.refresh(); })
            .catch(() => {
                this.setState({ starting: false });
                sweetalert.fire({ icon: "error", title: "", text: "Failed to retry export." });
            });
    }

    renderStatus(job: ExportJob) {
        if (job.Status === "ready") return <span className="badge badge-success">Ready</span>;
        if (job.Status === "failed") return <span className="badge badge-danger">Failed</span>;
        return <span className="badge badge-warning">Preparing</span>; // pending / processing
    }

    formatWhen(v?: string) {
        if (!v) return "";
        const d = new Date(v);
        return isNaN(d.getTime()) ? "" : d.toLocaleString();
    }

    render() {
        const { jobs } = this.state;
        return (
            <div className="modal fade form_modal" id={ExemptionExportsModal.ID} tabIndex={-1} role="dialog" aria-hidden="true">
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title verlag-bold">Exports</h5>
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close"
                                onClick={() => hideModal(ExemptionExportsModal.ID)}>
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <span className="text-muted" style={{ fontSize: "0.9em" }}>
                                    Choose an export below. The file is prepared in the background and appears in the
                                    list when it's ready.
                                </span>
                                <div className="mt-2 d-flex flex-wrap" style={{ gap: "0.5rem" }}>
                                    <button className="btn immySubmitButtonOutline verlag-bold" style={{ whiteSpace: "nowrap" }}
                                        disabled={this.state.starting} onClick={this.handleStartExport}>
                                        Export current results
                                    </button>
                                    <button className="btn immySubmitButtonOutline verlag-bold" style={{ whiteSpace: "nowrap" }}
                                        disabled={this.state.starting} onClick={this.handleStartExpiredExport}>
                                        Export expiring 6th-grade exemptions
                                    </button>
                                </div>
                            </div>

                            <h6 className="verlag-bold">Recent exports</h6>
                            {(!jobs || jobs.length === 0) ? (
                                <p className="text-muted mb-0">No exports yet. Click "Export current results" to create one.</p>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm table-striped">
                                        <thead>
                                            <tr>
                                                <th>Created</th>
                                                <th>Type</th>
                                                <th>Rows</th>
                                                <th>Status</th>
                                                <th aria-label="Download or retry actions"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {jobs.map((job) => (
                                                <tr key={job.ID}>
                                                    <td>{this.formatWhen(job.CreatedDate)}</td>
                                                    <td>{exportTypeLabel(job.ExportType)}</td>
                                                    <td>{job.Status === "ready" && job.RowCount != null ? job.RowCount : "-"}</td>
                                                    <td>{this.renderStatus(job)}</td>
                                                    <td className="text-right">
                                                        {job.Status === "ready" && (
                                                            <button className="btn btn-sm btn-outline-success"
                                                                onClick={() => this.handleDownload(job)}>Download</button>
                                                        )}
                                                        {job.Status === "failed" && (
                                                            <button className="btn btn-sm btn-outline-secondary"
                                                                onClick={() => this.handleRetry(job)}>Retry</button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
